import React, { useMemo, useState } from 'react'
import SALink from 'components/SALink'
import { slugify } from 'lib/slugify'
import ArtworkDescriptionConnector, {
  ConnectorProps as ArtworkDescriptionProps,
} from '../../connectors/ArtworkDescription'
import {
  getLinkCategory,
  displayEllipticalString,
  displayStringWithoutLineBreak,
} from '../../helpers'
import artworkStyles from './ArtworkDescription.module.scss'
import InfoTitleValue from './components/InfoTitleValue/InfoTitleValue'
import classNames from 'classnames'
import { OPEN_EDITION } from '../../helpers/constants'

const AboutArtwork = (props: ArtworkDescriptionProps) => {
  const {
    category,
    description,
    subject,
    styles,
    mediums,
    materials,
    currentTab,
    yearProduced,
  } = props
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false)
  const linkCategory = getLinkCategory(category)
  const linkCategoryType =
    currentTab === 'original' || currentTab === 'info' ? linkCategory : 'Prints'
  const cleanDescription = description.includes('\n')
    ? displayStringWithoutLineBreak(description)
    : description
  const isPrintArtwork = currentTab === OPEN_EDITION
  const isDescriptionLarge = description.length > 300

  const descriptionText = useMemo(() => {
    return isDescriptionOpen
      ? description
      : description.length > 300
      ? displayEllipticalString(cleanDescription, 300)
      : cleanDescription
  }, [isDescriptionOpen])

  const handleReadMoreOrLess = () => {
    setIsDescriptionOpen((oldValue) => !oldValue)
  }

  const uniqueStyle = Array.from(new Set([...(styles || [])]))
  const uniqueMediumAndMaterials = useMemo(() => {
    const uniqueMedium = Array.from(new Set([...(mediums || [])]))
    const uniqueMaterial = Array.from(new Set([...(materials || [])]))
    return [...uniqueMedium, ...uniqueMaterial]
  }, [mediums, materials])

  return (
    <div className={artworkStyles.artworkInfoWrapper}>
      <div className={artworkStyles.artworkInfoContainer}>
        <div className={artworkStyles.artworkDescriptionContainer}>
          <p
            className={classNames(artworkStyles.descriptionBox)}
            data-type='description'
            dangerouslySetInnerHTML={{
              __html: `${descriptionText.replace(/\n/g, '<br />')}`,
            }}
          />
          {isDescriptionLarge && (
            <button className={artworkStyles.readMoreOrLess} onClick={handleReadMoreOrLess}>
              {isDescriptionOpen ? 'READ LESS' : 'READ MORE'}
            </button>
          )}
        </div>

        {yearProduced && <InfoTitleValue title='Year Created:' value={yearProduced} />}
        {subject.length && (
          <InfoTitleValue
            title='Subject:'
            value={
              <SALink
                className={classNames(artworkStyles.artworkInfoLink, artworkStyles.linkInline)}
                href={`/${slugify(linkCategoryType)}/${slugify(subject)}`}
                title={`${subject} ${linkCategoryType}`}
              >
                <p className={artworkStyles.linkUnderline}>{subject}</p>
              </SALink>
            }
          />
        )}
        {styles.length && (
          <InfoTitleValue
            title='Styles:'
            value={uniqueStyle.map((style, styleIndex) => (
              <SALink
                key={style}
                className={classNames(artworkStyles.artworkInfoLink, artworkStyles.linkInline)}
                href={`/${slugify(linkCategoryType)}/${slugify(style)}`}
                title={`${style} ${linkCategoryType}`}
              >
                <p className={artworkStyles.linkUnderline}>{`${style}`}</p>
                <p>{`${styleIndex !== uniqueStyle.length - 1 ? ',' : ''}`}&nbsp;</p>
              </SALink>
            ))}
          />
        )}
        {!isPrintArtwork && mediums.length && (
          <InfoTitleValue
            title='Mediums:'
            value={uniqueMediumAndMaterials.map((mediumOrMaterial, mediumOrMaterialIndex) => (
              <SALink
                key={mediumOrMaterial}
                className={classNames(artworkStyles.artworkInfoLink, artworkStyles.linkInline)}
                href={`/${slugify(linkCategoryType)}/${slugify(mediumOrMaterial)}`}
                title={`${mediumOrMaterial} ${linkCategoryType}`}
              >
                <p className={artworkStyles.linkUnderline}>{`${mediumOrMaterial}`}</p>
                <p>
                  {`${mediumOrMaterialIndex !== uniqueMediumAndMaterials.length - 1 ? ',' : ''}`}
                  &nbsp;
                </p>
              </SALink>
            ))}
          />
        )}
      </div>
    </div>
  )
}

export default ArtworkDescriptionConnector(AboutArtwork)
